import { memo, useEffect, useRef, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import { Line } from "react-chartjs-2";
import { useViewport } from "@xyflow/react";
import { useSelector } from "react-redux";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
} from "chart.js";
import annotationPlugin from "chartjs-plugin-annotation";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  annotationPlugin
);

/**
 * MiniGraphWidget – SCADA-style live monitoring mini graph.
 *
 * Renders as a detached floating widget with a professional
 * industrial monitoring chart. Uses react-chartjs-2 (already installed).
 *
 * Features:
 * - Smooth line chart with gradient fill
 * - Rolling 20-point data buffer
 * - X-axis time labels (HH:MM:SS)
 * - Y-axis value scale
 * - Configurable threshold lines (warning/critical)
 * - Subtle grid with industrial appearance
 * - Live data append with left-shift behavior
 * - Smooth animation transitions
 * - Memoized for performance
 *
 * Supports: Voltage, RPM, Temperature, Pressure, Flow rate, Percentage
 */
const MAX_POINTS = 30;

const MiniGraphWidget = memo(({
  value = 0,
  historyData = [],
  min = 0,
  max = 100,
  unit = "",
  color = "#3b82f6",
  warningThreshold,
  criticalThreshold,
  width,
  height,
  label = "",
}) => {
  const { isDark } = useSelector((state) => state.theme);
  // Lazy initialization of the rolling data buffer
  const dataRef = useRef(null);
  if (!dataRef.current) {
    const now = new Date();
    const timeLabel = now.toLocaleTimeString("en-US", {
      hour12: false,
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
    const numVal = Number(value) || 0;
    const buf = { values: [], labels: [] };

    // If history is provided, seed from history
    if (historyData && historyData.length > 0) {
      const pointsToTake = MAX_POINTS - 1;
      const lastPoints = historyData.slice(-pointsToTake);
      
      const deficit = pointsToTake - lastPoints.length;
      if (deficit > 0) {
        const firstTime = new Date(lastPoints.length > 0 ? lastPoints[0].x : now.getTime() - pointsToTake * 60000);
        const firstVal = lastPoints.length > 0 ? lastPoints[0].y : numVal;
        for (let i = 0; i < deficit; i++) {
          buf.values.push(firstVal);
          const seedTime = new Date(firstTime.getTime() - (deficit - i) * 60000);
          buf.labels.push(
            seedTime.toLocaleTimeString("en-US", { hour12: false, hour: "2-digit", minute: "2-digit", second: "2-digit" })
          );
        }
      }
      
      for (const point of lastPoints) {
        buf.values.push(point.y);
        buf.labels.push(
          new Date(point.x).toLocaleTimeString("en-US", { hour12: false, hour: "2-digit", minute: "2-digit", second: "2-digit" })
        );
      }
    } else {
      // Fallback: Seed buffer with current value
      for (let i = 0; i < MAX_POINTS - 1; i++) {
        buf.values.push(numVal);
        const seedTime = new Date(now.getTime() - (MAX_POINTS - i) * 10000);
        buf.labels.push(
          seedTime.toLocaleTimeString("en-US", {
            hour12: false,
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
          })
        );
      }
    }
    
    // Append the current live value as the final point
    buf.values.push(numVal);
    buf.labels.push(timeLabel);
    
    dataRef.current = buf;
  }

  // Chart ref for imperative updates (avoids full re-render)
  const chartRef = useRef(null);

  // Append new data point on value change
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    const timeLabel = new Date().toLocaleTimeString("en-US", {
      hour12: false,
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });

    const buf = dataRef.current;
    const numVal = Number(value) || 0;

    // Append and shift
    buf.values.push(numVal);
    buf.labels.push(timeLabel);
    if (buf.values.length > MAX_POINTS) {
      buf.values.shift();
      buf.labels.shift();
    }

    // Update chart imperatively for performance
    const chart = chartRef.current;
    if (chart) {
      chart.data.labels = [...buf.labels];
      chart.data.datasets[0].data = [...buf.values];
      chart.update("none"); // "none" mode skips animation for smoother updates
    }
  }, [value]);

  // Compute threshold annotations
  const annotations = useMemo(() => {
    const result = {};

    if (warningThreshold != null) {
      result.warningLine = {
        type: "line",
        yMin: warningThreshold,
        yMax: warningThreshold,
        borderColor: "#ef4444",
        borderWidth: 1.5,
        borderDash: [],
        label: {
          display: false,
        },
      };
    }

    if (criticalThreshold != null) {
      result.criticalLine = {
        type: "line",
        yMin: criticalThreshold,
        yMax: criticalThreshold,
        borderColor: "#eab308",
        borderWidth: 1.5,
        borderDash: [],
        label: {
          display: false,
        },
      };
    }

    return result;
  }, [warningThreshold, criticalThreshold]);

  // Solid fill factory
  const getGradient = useCallback(
    () => {
      return `${color}66`; // Solid light blue fill with opacity
    },
    [color]
  );

  // Chart data config
  const chartData = useMemo(
    () => ({
      labels: dataRef.current.labels.length > 0 ? [...dataRef.current.labels] : Array(MAX_POINTS).fill(""),
      datasets: [
        {
          data: dataRef.current.values.length > 0 ? [...dataRef.current.values] : Array(MAX_POINTS).fill(0),
          borderColor: color,
          borderWidth: Math.max(1.5, (height || 140) * 0.012),
          backgroundColor: getGradient,
          fill: true,
          tension: 0,
          pointRadius: Math.max(1.5, (height || 140) * 0.012),
          pointBackgroundColor: color,
          pointHoverRadius: Math.max(3, (height || 140) * 0.025),
          pointHoverBackgroundColor: color,
          pointHoverBorderColor: "#fff",
          pointHoverBorderWidth: Math.max(0.5, (height || 140) * 0.005),
        },
      ],
    }),
    [color, getGradient]
  );

  const { zoom } = useViewport();

  // Chart options
  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      resizeDelay: 0,      // Immediate redraw on size change
      animation: false,    // Disable initial animation which can cause bounding box issues in scaled containers
      // Smooth devicePixelRatio: standard DPR when zoomed out, scale up only when zoomed in
      devicePixelRatio: zoom > 1 ? Math.min((window.devicePixelRatio || 1) * zoom, 3) : (window.devicePixelRatio || 1),
      interaction: {
        intersect: false,
        mode: "index",
      },
      layout: {
        padding: { top: 4, right: 8, bottom: 0, left: 4 },
      },
      scales: {
        x: {
          display: true,
          ticks: {
            maxTicksLimit: 5,
            font: { 
              size: Math.max(9, (height || 140) * 0.06), 
              family: "'Inter', 'Segoe UI', sans-serif",
              weight: "500"
            },
            color: isDark ? "#cbd5e1" : "#374151",
            maxRotation: 0,
          },
          grid: {
            color: isDark ? "rgba(255, 255, 255, 0.1)" : "#e2e8f020",
            lineWidth: 0.5,
          },
          border: { display: false },
        },
        y: {
          display: true,
          ticks: {
            maxTicksLimit: 5,
            font: { 
              size: Math.max(9, (height || 140) * 0.06), 
              family: "'Inter', 'Segoe UI', sans-serif",
              weight: "500"
            },
            color: isDark ? "#cbd5e1" : "#374151",
            callback: (v) => v.toFixed(0),
          },
          grid: {
            color: isDark ? "rgba(255, 255, 255, 0.15)" : "#e2e8f030",
            lineWidth: 0.5,
          },
          border: { display: false },
        },
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: "#1e293bdd",
          titleFont: { 
            size: Math.max(10, (height || 140) * 0.07), 
            family: "'Inter', sans-serif" 
          },
          bodyFont: { 
            size: Math.max(12, (height || 140) * 0.09), 
            family: "'Inter', sans-serif", 
            weight: "bold" 
          },
          padding: Math.max(4, (height || 140) * 0.03),
          cornerRadius: 4,
          displayColors: false,
          callbacks: {
            label: (ctx) => `${ctx.parsed.y.toFixed(1)} ${unit}`,
          },
        },
        annotation: {
          annotations,
        },
      },
    }),
    [unit, annotations, zoom, height, isDark]
  );

  return (
    <div className="scada-widget scada-widget--graph" style={{ width: width || '100%', height: height || '100%' }}>
      {label && <div className="scada-widget__label" style={{ fontSize: Math.max(8, (height || 140) * 0.06), marginBottom: 2 }}>{label}</div>}
      {/* Chart container */}
      <div className="scada-widget__graph-container" style={{ height: '100%' }}>
        {/* Key on zoomTier forces Chart.js to re-init the canvas with correct devicePixelRatio when zooming in */}
        <Line key={`mg-${zoom > 1 ? Math.ceil(zoom) : 1}`} ref={chartRef} data={chartData} options={chartOptions} />
      </div>
    </div>
  );
});

MiniGraphWidget.displayName = "MiniGraphWidget";

MiniGraphWidget.propTypes = {
  value: PropTypes.number,
  historyData: PropTypes.array,
  min: PropTypes.number,
  max: PropTypes.number,
  unit: PropTypes.string,
  color: PropTypes.string,
  label: PropTypes.string,
  warningThreshold: PropTypes.number,
  criticalThreshold: PropTypes.number,
  width: PropTypes.number,
  height: PropTypes.number,
};

export default MiniGraphWidget;